import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import {
  Box,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import FilePresentIcon from "@mui/icons-material/FilePresent";
import { Document, Page, pdfjs } from "react-pdf";
import moment from "moment";
import { useWindowWidth } from "@wojtekmaj/react-hooks";

import reultsService from "../../../Services/resultsService";
import "react-pdf/dist/esm/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.js`;

function Hafnayot(props) {
  const theme = useTheme();
  const width = useWindowWidth();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [invoiceSelected, setHafnayotSelected] = useState();
  const [pdfString, setPdfString] = useState();
  const [loading, setLoading] = useState(false);

  const resultList = props.hafnayot || [];

  useEffect(() => {
    if (invoiceSelected) {
      setLoading(true);
      reultsService
        .getHafnayotFile(invoiceSelected.docName)
        .then((p) => {
          setPdfString(p.data);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    } else if (resultList.length > 0) {
      setHafnayotSelected(resultList[0]);
    }
  }, [invoiceSelected, resultList]);

  const downloadPDF = () => {
    if (!pdfString) return;
    const linkSource = `data:application/pdf;base64,${pdfString}`;
    const downloadLink = document.createElement("a");
    downloadLink.href = linkSource;
    downloadLink.download = `Hafnaya_${invoiceSelected?.docNum || "file"}.pdf`;
    downloadLink.click();
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "1100px",
        margin: "0 auto",
        p: { xs: 2, md: 4 },
        boxSizing: "border-box",
        direction: "rtl",
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          mb: 4,
          color: theme.palette.text.primary,
          textAlign: "left",
        }}
      >
        הפניות רפואיות
      </Typography>

      {resultList.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: "center", borderRadius: 3 }}>
          <Typography variant="h6" color="textSecondary">
            אין נתונים להצגה
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* שורת בחירה והורדה */}
          <Paper
            elevation={2}
            sx={{
              p: 2.5,
              borderRadius: 3,
              // משתמש בצבע הראשי של ה-Theme לקו המדגיש
              borderRight: `6px solid ${theme.palette.primary.main}`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: "center",
                gap: 2,
              }}
            >
              {/* כפתור ההורדה - מושך צבעים מה-Theme */}
              <Button
                variant="contained"
                color="primary"
                startIcon={<DownloadIcon sx={{ ml: 1, mr: 0 }} />}
                onClick={downloadPDF}
                disabled={!pdfString}
                sx={{
                  borderRadius: "12px",
                  height: "56px",
                  px: 4,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  width: { xs: "100%", md: "auto" },
                  boxShadow: `0 4px 12px ${theme.palette.primary.main}33`, // 33 מוסיף שקיפות לצל
                }}
              >
                הורד מסמך PDF
              </Button>

              {/* תיבת הבחירה */}
              <Box sx={{ flex: 1, width: "100%" }}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="select-hafnaya-label">
                    בחר הפניה לצפייה
                  </InputLabel>
                  <Select
                    labelId="select-hafnaya-label"
                    value={invoiceSelected?.docNum || ""}
                    label="בחר הפניה לצפייה"
                    onChange={(e) => {
                      const selected = resultList.find(
                        (item) => item.docNum === e.target.value,
                      );
                      setHafnayotSelected(selected);
                    }}
                    sx={{ borderRadius: "12px" }}
                  >
                    {resultList.map((e) => (
                      <MenuItem key={e.docNum} value={e.docNum}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{ mr: 4 }}
                          >
                            {moment(e.devdatetime).format("DD/MM/YYYY")}
                          </Typography>
                          <Typography sx={{ fontWeight: 600 }}>
                            {e.title}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Paper>

          {/* תצוגת ה-PDF */}
          <Paper
            elevation={3}
            sx={{
              p: { xs: 1, md: 3 },
              borderRadius: 3,
              minHeight: "800px",
              // רקע כהה מקצועי שמותאם ל-Theme
              backgroundColor: theme.palette.grey[700],
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                mb: 2.5,
                color: theme.palette.common.white,
              }}
            >
              <FilePresentIcon />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {invoiceSelected?.title} |{" "}
                {moment(invoiceSelected?.devdatetime).format("DD/MM/YYYY")}
              </Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                bgcolor: theme.palette.background.paper,
                borderRadius: 2,
                overflow: "auto",
                p: { xs: 0, md: 2 },
                boxShadow: "inset 0 0 20px rgba(0,0,0,0.1)",
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    py: 20,
                  }}
                >
                  <CircularProgress color="primary" sx={{ mb: 2 }} />
                  <Typography color="textSecondary">טוען קובץ...</Typography>
                </Box>
              ) : pdfString ? (
                <Document
                  file={`data:application/pdf;base64,${pdfString}`}
                  loading={<CircularProgress color="primary" />}
                >
                  <Page
                    pageNumber={1}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    width={isMobile ? width * 0.85 : 900}
                  />
                </Document>
              ) : (
                <Typography sx={{ mt: 10 }} color="textSecondary">
                  לא נבחר קובץ להצגה
                </Typography>
              )}
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
}

const mapStateToProps = (state) => ({ hafnayot: state.hafnayot });
export default connect(mapStateToProps)(Hafnayot);
